import { BRAND } from '@onuw/roles-data'
import type { ComponentType } from 'react'
import type { GuideSlug } from '../guideSlugs'
import { Bots } from './Bots'
import { Decks } from './Decks'
import { Alien, BaseGame, BonusRoles, Daybreak, Vampire } from './Expansions'
import { MarksAndArtifacts } from './MarksAndArtifacts'
import { NightOrder } from './NightOrder'
import { RemoteGameNight } from './RemoteGameNight'
import { Strategy } from './Strategy'

export interface Guide {
  slug: GuideSlug
  /** The page's heading, and its name in lists. */
  title: string
  /** Under the heading. */
  lead: string
  /** For search results and link previews. */
  description: string
  /** How to play the game, or what a pack of roles adds to it. */
  group: 'play' | 'pack'
  Body: ComponentType
}

/** Every guide under /guides/. The type fails when a slug of GUIDE_SLUGS has none. */
export const GUIDES: Record<GuideSlug, Guide> = {
  'night-order': {
    slug: 'night-order',
    title: 'The night order',
    lead: 'Every role wakes at a fixed moment. Knowing the order tells you whose information is still true in the morning.',
    description: `The complete wake order of ${BRAND.name}: every role from dusk to dawn, what each one does, and why the order decides which claims can be true.`,
    group: 'play',
    Body: NightOrder,
  },
  strategy: {
    slug: 'strategy',
    title: 'Strategy for your first games',
    lead: 'One night of half-knowledge, a few minutes of talk, one vote. Here is how to use them.',
    description: `How to win ${BRAND.name} as a villager, a Werewolf or a Tanner: what to claim, how to rebuild the night from the claims, and the mistakes everyone makes once.`,
    group: 'play',
    Body: Strategy,
  },
  decks: {
    slug: 'decks',
    title: 'Decks for every table',
    lead: 'Which roles to deal, from three players to twelve, and how to build a deck of your own.',
    description: `Recommended ${BRAND.name} role setups for 3 to 12 players, the ready-made decks of the online table, and rules of thumb for balancing your own.`,
    group: 'play',
    Body: Decks,
  },
  'marks-and-artifacts': {
    slug: 'marks-and-artifacts',
    title: 'Marks and artifacts',
    lead: 'The tokens beside the cards: what each mark and artifact does, who hands them out, and how they change the vote.',
    description: `Every mark and artifact in ${BRAND.name}: the Mark of the Vampire, Love, Fear, Disease, the Traitor and the Assassin, the Curator's nine artifacts and the Sentinel's shield, with rulings.`,
    group: 'play',
    Body: MarksAndArtifacts,
  },
  'remote-game-night': {
    slug: 'remote-game-night',
    title: 'Hosting a game night',
    lead: 'In one room, over a video call, or half and half: how to set the table up so the game runs itself.',
    description: `How to host ${BRAND.name} online: inviting players, seat order, timers, voice and video for remote and mixed groups, and what to do when a connection drops.`,
    group: 'play',
    Body: RemoteGameNight,
  },
  bots: {
    slug: 'bots',
    title: 'How the bots play',
    lead: 'They claim roles, lie when they are wolves, answer your questions and vote. What they know, and how to play against them.',
    description: `Play ${BRAND.name} alone or fill empty seats: how the bots claim, bluff, reason and vote, what they know about the game, and how to get a good game out of them.`,
    group: 'play',
    Body: Bots,
  },
  'base-game': {
    slug: 'base-game',
    title: 'The base game',
    lead: 'Twelve roles, two Werewolves, one night. Where every table starts.',
    description: `The twelve roles of the ${BRAND.name} base game, what each one teaches, and the deck to learn with.`,
    group: 'pack',
    Body: BaseGame,
  },
  daybreak: {
    slug: 'daybreak',
    title: 'Daybreak',
    lead: 'Eleven roles that sharpen the base game: new wolves, a shield, artifacts and a card turned face up.',
    description: `The Daybreak roles in ${BRAND.name}: the Alpha Wolf, the Mystic Wolf, the Witch, the Revealer, the Curator and the rest, with what they add and a deck to try them in.`,
    group: 'pack',
    Body: Daybreak,
  },
  'bonus-roles': {
    slug: 'bonus-roles',
    title: 'Bonus roles',
    lead: 'Single cards that drop into any deck, each with one sharp idea.',
    description: `The bonus roles in ${BRAND.name}: the Aura Seer, the Prince, the Cursed, Nostradamus, the Squire, the Body Snatcher and more, and where each one fits.`,
    group: 'pack',
    Body: BonusRoles,
  },
  vampire: {
    slug: 'vampire',
    title: 'The Vampire pack',
    lead: 'A second evil team, and marks: dusk comes before the night, and a mark can change your side.',
    description: `The Vampire roles in ${BRAND.name}: the Vampires, the Count, the Master, Cupid, the Assassin and the marks they hand out at dusk, with a deck to start with.`,
    group: 'pack',
    Body: Vampire,
  },
  alien: {
    slug: 'alien',
    title: 'The Alien pack',
    lead: 'Roles whose powers change every game: the app draws an instruction and reads it to the whole table.',
    description: `The Alien roles in ${BRAND.name}: the Aliens, Groob and Zerb, the Synthetic Alien, the Oracle, the Psychic and the app's announced instructions, with a deck to start with.`,
    group: 'pack',
    Body: Alien,
  },
}
